/**
 * In-memory mock API for local workshop demos (VITE_USE_MOCK=true).
 * Reset on full page reload.
 */

import {
  ADMIN_TEST_UNIVERSITY,
  canSeeAdminTestUniversity,
  isAdminTestUniversity,
} from './adminTestUniversity.js'

const store = {
  users: [
    {
      sub: 'mock-student-sub',
      email: 'student@example.com',
      name: 'Demo Student',
      status: 'approved',
      createdAt: '2026-01-01T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      requestedCollegeName: null,
    },
    {
      sub: 'mock-pending-sub',
      email: 'pending@example.com',
      name: 'Pending Student',
      status: 'pending',
      createdAt: '2026-01-02T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      requestedCollegeName: null,
    },
    {
      sub: 'mock-removed-sub',
      email: 'removed@example.com',
      name: 'Removed Student',
      status: 'removed',
      createdAt: '2026-01-03T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      requestedCollegeName: null,
    },
    {
      sub: 'mock-admin-sub',
      email: 'admin@example.com',
      name: 'Facilitator Admin',
      status: 'approved',
      createdAt: '2026-01-01T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      requestedCollegeName: null,
    },
  ],
  colleges: [
    {
      id: 'demo-stanford-2026',
      name: 'Stanford University',
      enabled: true,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'demo-mit-2026',
      name: 'MIT',
      enabled: true,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'demo-unscheduled',
      name: 'Reed College',
      enabled: true,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'demo-disabled',
      name: 'Disabled College Demo',
      enabled: false,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
  ],
  requests: [],
  votes: {}, // `${collegeId}:${slideId}:${sub}` -> choiceId | choiceIds[] (multi-select)
  aggregates: {}, // `${collegeId}:${slideId}` -> { choiceId: count }
  progress: {}, // sub -> { lastSlideId, updatedAt }
}

const ADMIN_EMAILS = new Set([
  'prashanth.ayyavu@gmail.com',
  'codeshipwatch@gmail.com',
  'silvia.r.muthumani@gmail.com',
  'admin@example.com',
])

function isAdminEmail(email) {
  return ADMIN_EMAILS.has((email || '').trim().toLowerCase())
}

function canSeeCollege(session, name) {
  if (!isAdminTestUniversity(name)) return true
  return canSeeAdminTestUniversity(session?.email, { isAdmin: !!session?.isAdmin })
}

function ensureAdminTestUniversity() {
  if (store.colleges.some(c => isAdminTestUniversity(c.name))) return
  store.colleges.push({
    id: 'admin-test-university', name: ADMIN_TEST_UNIVERSITY, enabled: true,
    createdAt: now(), updatedAt: now(),
  })
}

function now() {
  return new Date().toISOString()
}

function ensureUser(session) {
  let user = store.users.find((u) => u.sub === session.sub)
  if (!user) {
    user = {
      sub: session.sub,
      email: session.email,
      name: session.name,
      picture: session.picture,
      status: session.isAdmin ? 'approved' : 'pending',
      createdAt: now(),
      collegeId: null,
      collegeName: null,
      requestedCollegeName: null,
    }
    store.users.push(user)
  }
  return user
}









function resolveWorkshop(name, { enabledOnly = true } = {}) {
  const key = (name || '').trim().toLowerCase()
  const matches = store.colleges.filter(c => c.name.trim().toLowerCase() === key && (!enabledOnly || c.enabled))
  matches.sort((a, b) => Number(!a.enabled) - Number(!b.enabled) ||
    (a.createdAt || '').localeCompare(b.createdAt || '') || a.id.localeCompare(b.id))
  return matches[0] || null
}

function findCollegeForStudent(collegeId, collegeName, session) {
  const isAdmin = !!session?.isAdmin
  let college = null
  if (collegeId) {
    const found = store.colleges.find((c) => c.id === collegeId)
    if (found && (found.enabled || isAdmin)) college = found
  } else {
    college = resolveWorkshop(collegeName, { enabledOnly: !isAdmin })
  }
  if (college && !canSeeCollege(session, college.name)) return null
  return college
}



function mePayload(session, user) {
  let assigned = null
  let collegeName = null
  if (!session.isAdmin) {
    assigned = store.colleges.find(c => c.id === user.collegeId) || null
    if (assigned && !canSeeCollege(session, assigned.name)) assigned = null
    collegeName = canSeeCollege(session, user.collegeName) ? user.collegeName : null
    if (!assigned && !user.collegeId && user.status === 'approved' && collegeName) {
      assigned = resolveWorkshop(collegeName, { enabledOnly: false })
    }
    if (assigned) collegeName = assigned.name
  }
  return {
    sub: user.sub, email: user.email, name: user.name, picture: user.picture || null,
    status: session.isAdmin ? 'approved' : user.status, isAdmin: !!session.isAdmin,
    collegeId: assigned?.id || null, collegeName,
    sessionEnabled: !!assigned?.enabled, assigned: !!assigned,
    requestedCollegeName: session.isAdmin ? null : user.requestedCollegeName || null,
  }
}

function forbidden() {
  const err = new Error('Forbidden')
  err.status = 403
  throw err
}

function voteKey(collegeId, slideId, sub) {
  return `${collegeId}:${slideId}:${sub}`
}

function aggKey(collegeId, slideId) {
  return `${collegeId}:${slideId}`
}

function pollCollege(collegeId) {
  return store.colleges.find(c => c.id === collegeId) || null
}

export async function getMe(session) {
  const user = ensureUser(session)
  if (user.requestedCollegeName) {
    user.requestedCollegeName = null
  }
  if (session.isAdmin) {
    user.collegeId = null
    user.collegeName = null
    delete user.workshopDate
    user.requestedCollegeName = null
  }
  ensureAdminTestUniversity()
  const payload = mePayload(session, user)
  if (
    !session.isAdmin &&
    user.status === 'approved' &&
    payload.collegeId &&
    user.collegeId !== payload.collegeId
  ) {
    user.collegeId = payload.collegeId
    user.collegeName = payload.collegeName
    delete user.workshopDate
  }
  return payload
}

export async function setMyCollege(session, collegeIdOrOpts, collegeNameArg) {
  ensureAdminTestUniversity()
  const user = ensureUser(session)
  const opts =
    collegeIdOrOpts && typeof collegeIdOrOpts === 'object'
      ? collegeIdOrOpts
      : { collegeId: collegeIdOrOpts, collegeName: collegeNameArg }

  if (session.isAdmin) {
    const err = new Error('Admins are not assigned to a college')
    err.status = 400
    throw err
  }

  const name = (opts.collegeName || '').trim()
  if (!canSeeCollege(session, name)) {
    const err = new Error('College not found')
    err.status = 404
    throw err
  }
  const matches = store.colleges.filter((c) => c.name.toLowerCase() === name.toLowerCase())
  if (!name || !matches.length) {
    const err = new Error('College not found')
    err.status = 404
    throw err
  }
  const chosen = matches[0].name
  const sameCollege = (user.collegeName || '').toLowerCase() === chosen.toLowerCase()
  user.collegeName = chosen
  user.requestedCollegeName = null
  if (user.status === 'approved' && (sameCollege || isAdminTestUniversity(chosen))) {
    const existing = store.colleges.find(c => c.id === user.collegeId)
    const row = existing?.name.toLowerCase() === chosen.toLowerCase()
      ? existing : resolveWorkshop(chosen, { enabledOnly: false })
    user.collegeId = row?.id || null
  } else {
    user.collegeId = null
    user.status = 'pending'
  }
  delete user.workshopDate
  return getMe(session)
}

export async function requestCollege(session, collegeName) {
  const name = (collegeName || '').trim()
  if (!name) {
    const err = new Error('collegeName is required')
    err.status = 400
    throw err
  }
  const existing = findCollegeForStudent('', name, session)
  if (existing) {
    return setMyCollege(session, { collegeName: name })
  }
  const user = ensureUser(session)
  store.requests.push({
    id: `r-${Math.random().toString(36).slice(2, 10)}`,
    collegeName: name,
    sub: user.sub,
    email: user.email,
    name: user.name,
    createdAt: now(),
  })
  user.requestedCollegeName = null
  user.collegeId = null
  user.collegeName = null
  delete user.workshopDate
  if (user.status === 'removed') {
    user.status = 'pending'
  }
  return getMe(session)
}

export async function checkAdmin(session) {
  return { isAdmin: !!session.isAdmin }
}

export async function listColleges(session, { q, all } = {}) {
  ensureAdminTestUniversity()
  const query = (q || '').trim().toLowerCase()
  const seen = new Map()
  for (const c of store.colleges) {
    if (!(all && session.isAdmin) && !c.enabled) continue
    if (!canSeeCollege(session, c.name)) continue
    if (query && !c.name.toLowerCase().includes(query)) continue
    const key = c.name.toLowerCase()
    if (!seen.has(key)) seen.set(key, { name: c.name })
  }
  const colleges = [...seen.values()].sort((a, b) => a.name.localeCompare(b.name))
  return { colleges }
}

export async function adminListColleges(session) {
  if (!session.isAdmin) forbidden()
  ensureAdminTestUniversity()
  return { colleges: store.colleges.filter(c => canSeeCollege(session, c.name))
    .map(({ id, name, enabled, createdAt, updatedAt }) => ({ id, name, enabled, createdAt, updatedAt }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id)) }
}

export async function adminListCollegeRequests(session) {
  if (!session.isAdmin) forbidden()
  const requests = [...store.requests].sort((a, b) =>
    String(b.createdAt).localeCompare(String(a.createdAt)),
  )
  return { requests }
}

export async function adminCreateCollege(session, { name, enabled = true }) {
  if (!session.isAdmin) forbidden()
  const n = (name || '').trim()
  if (!n) throw Object.assign(new Error('name is required'), { status: 400 })
  if (store.colleges.some(c => c.name.toLowerCase() === n.toLowerCase())) {
    throw Object.assign(new Error('A college with this name already exists'), { status: 409 })
  }
  const college = { id: `c-${Math.random().toString(36).slice(2, 10)}`, name: n,
    enabled: !!enabled, createdAt: now(), updatedAt: now() }
  store.colleges.push(college)
  return { college }
}

export async function adminUpdateCollege(session, id, patch) {
  if (!session.isAdmin) forbidden()
  const college = store.colleges.find(c => c.id === id)
  if (!college || !canSeeCollege(session, college.name)) {
    throw Object.assign(new Error('College not found'), { status: 404 })
  }
  const name = patch.name === undefined ? college.name : String(patch.name).trim()
  if (!name) throw Object.assign(new Error('name cannot be empty'), { status: 400 })
  if (name.toLowerCase() !== college.name.toLowerCase() && store.colleges.some(c =>
    c.id !== id && c.name.toLowerCase() === name.toLowerCase())) {
    throw Object.assign(new Error('A college with this name already exists'), { status: 409 })
  }
  college.name = name
  if (patch.enabled !== undefined) college.enabled = !!patch.enabled
  delete college.workshopDate
  delete college.everyday
  college.updatedAt = now()
  return { college: { ...college } }
}

export async function adminDeleteCollege(session, id) {
  if (!session.isAdmin) forbidden()
  const idx = store.colleges.findIndex((c) => c.id === id)
  if (idx < 0 || !canSeeCollege(session, store.colleges[idx].name)) {
    const err = new Error('College not found')
    err.status = 404
    throw err
  }
  store.colleges.splice(idx, 1)
  return { deleted: true, id }
}

function userPublic(user) {
  const admin = isAdminEmail(user.email)
  return {
    ...Object.fromEntries(Object.entries(user).filter(([key]) => key !== 'workshopDate')),
    isAdmin: admin,
    collegeId: admin ? null : user.collegeId,
    collegeName: admin ? null : user.collegeName,
    requestedCollegeName: admin ? null : user.requestedCollegeName,
  }
}

export async function listUsers(session, { status } = {}) {
  if (!session.isAdmin) forbidden()
  let users = store.users.map(userPublic)
  if (status && status !== 'all') {
    users = users.filter((u) => u.status === status)
  }
  if (!status || status === 'all') {
    const seen = new Set(users.map((u) => (u.email || '').toLowerCase()))
    for (const email of [...ADMIN_EMAILS].sort()) {
      if (seen.has(email)) continue
      users.push({
        sub: '',
        email,
        name: '',
        status: 'admin',
        isAdmin: true,
        collegeId: null,
        collegeName: null,
        requestedCollegeName: null,
      })
    }
  }
  return { users, status: status || 'pending' }
}

export async function approveUser(session, sub, { sessionId, collegeId, collegeName } = {}) {
  if (!session.isAdmin) forbidden()
  const user = store.users.find(u => u.sub === sub)
  if (!user) throw Object.assign(new Error('Not found'), { status: 404 })
  if (isAdminEmail(user.email)) throw Object.assign(new Error('Admins are not assigned to a college'), { status: 400 })
  const name = collegeName || user.collegeName || user.requestedCollegeName
  const explicitId = sessionId || collegeId
  const existing = pollCollege(user.collegeId)
  const row = explicitId ? pollCollege(explicitId)
    : existing?.name.toLowerCase() === name?.toLowerCase() ? existing
    : resolveWorkshop(name, { enabledOnly: false })
  if (!row) throw Object.assign(new Error('Select a college before approving the student'), { status: 400 })
  if (!canSeeCollege({ email: user.email }, row.name)) {
    throw Object.assign(new Error('This university is limited to specific accounts'), { status: 400 })
  }
  Object.assign(user, { status: 'approved', approvedBy: session.sub, approvedAt: now(),
    collegeId: row.id, collegeName: row.name, requestedCollegeName: null })
  delete user.workshopDate
  return { user: userPublic(user) }
}

export async function removeUser(session, sub) {
  if (!session.isAdmin) forbidden()
  const user = store.users.find((u) => u.sub === sub)
  if (!user) {
    const err = new Error('Not found')
    err.status = 404
    throw err
  }
  if (isAdminEmail(user.email)) {
    const err = new Error('Admins are not assigned to a college')
    err.status = 400
    throw err
  }
  user.status = 'removed'
  // Drop the session so the user signs up again (college name).
  user.collegeId = null
  user.collegeName = null
  delete user.workshopDate
  user.requestedCollegeName = null
  user.approvedBy = session.sub
  user.approvedAt = now()
  return { user }
}

// `choice` is a single choiceId or an array of choiceIds (multi-select polls).
// Each selected choice counts once toward the aggregate; a user still votes once.
export async function vote(session, slideId, choice) {
  const user = ensureUser(session)
  if (!session.isAdmin && user.status !== 'approved') forbidden()
  const row = pollCollege(user.collegeId)
  if (row && !canSeeCollege(session, row.name)) forbidden()
  if (!row) {
    const err = new Error('Select a college before voting')
    err.status = 400
    throw err
  }
  const key = voteKey(row.id, slideId, session.sub)
  if (store.votes[key]) {
    const err = new Error('Already voted')
    err.status = 409
    throw err
  }
  const isMulti = Array.isArray(choice)
  const choiceIds = isMulti ? [...new Set(choice)] : [choice]
  store.votes[key] = isMulti ? choiceIds : choice
  const ak = aggKey(row.id, slideId)
  if (!store.aggregates[ak]) {
    store.aggregates[ak] = {}
  }
  for (const cid of choiceIds) {
    store.aggregates[ak][cid] = (store.aggregates[ak][cid] || 0) + 1
  }
  return {
    slideId,
    collegeId: row.id,
    collegeName: row.name,
    ...(isMulti ? { choiceIds } : { choiceId: choice }),
    votedAt: now(),
  }
}

export async function getMyVote(session, slideId) {
  const user = ensureUser(session)
  if (!session.isAdmin && user.status !== 'approved') forbidden()
  const row = pollCollege(user.collegeId)
  if (row && !canSeeCollege(session, row.name)) forbidden()
  if (!row) {
    return { vote: null }
  }
  const key = voteKey(row.id, slideId, session.sub)
  const val = store.votes[key]
  if (!val) {
    return { slideId, collegeId: row.id, vote: null }
  }
  const vote = Array.isArray(val)
    ? { slideId, choiceIds: val }
    : { slideId, choiceId: val }
  return { slideId, collegeId: row.id, vote }
}

export async function getPollResults(session, slideId, { collegeId, collegeName } = {}) {
  if (!session.isAdmin) forbidden()
  const row = collegeId ? pollCollege(collegeId) : resolveWorkshop(collegeName, { enabledOnly: false })
  if (row && !canSeeCollege(session, row.name)) forbidden()
  if (!row) {
    const err = new Error('Select a college to view results')
    err.status = 400
    throw err
  }
  const counts = store.aggregates[aggKey(row.id, slideId)] || {}
  const total = Object.values(counts).reduce((a, b) => a + b, 0)
  return {
    slideId,
    collegeId: row.id,
    collegeName: row.name,
    counts,
    total,
  }
}

export async function clearPollResults(session, slideId, { collegeId, collegeName } = {}) {
  if (!session.isAdmin) forbidden()
  const row = collegeId ? pollCollege(collegeId) : resolveWorkshop(collegeName, { enabledOnly: false })
  if (row && !canSeeCollege(session, row.name)) forbidden()
  if (!row) {
    const err = new Error('Select a college to clear results')
    err.status = 400
    throw err
  }
  const prefix = `${row.id}:${slideId}`
  let deleted = 0
  for (const key of Object.keys(store.votes)) {
    if (key.startsWith(`${prefix}:`)) {
      delete store.votes[key]
      deleted += 1
    }
  }
  const ak = aggKey(row.id, slideId)
  if (store.aggregates[ak]) {
    delete store.aggregates[ak]
    deleted += 1
  }
  return {
    slideId,
    collegeId: row.id,
    collegeName: row.name,
    deleted,
    counts: {},
    total: 0,
  }
}

export async function getProgress(session) {
  const user = ensureUser(session)
  if (!session.isAdmin && user.status !== 'approved') forbidden()
  return store.progress[session.sub] || { lastSlideId: null, updatedAt: null }
}

export async function putProgress(session, lastSlideId) {
  const user = ensureUser(session)
  if (!session.isAdmin && user.status !== 'approved') forbidden()
  store.progress[session.sub] = { lastSlideId, updatedAt: now() }
  return store.progress[session.sub]
}

/** Test helper — wipe in-memory state */
export function __resetStore() {
  store.users = [
    {
      sub: 'mock-student-sub',
      email: 'student@example.com',
      name: 'Demo Student',
      status: 'approved',
      createdAt: '2026-01-01T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      requestedCollegeName: null,
    },
    {
      sub: 'mock-pending-sub',
      email: 'pending@example.com',
      name: 'Pending Student',
      status: 'pending',
      createdAt: '2026-01-02T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      requestedCollegeName: null,
    },
    {
      sub: 'mock-removed-sub',
      email: 'removed@example.com',
      name: 'Removed Student',
      status: 'removed',
      createdAt: '2026-01-03T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      requestedCollegeName: null,
    },
    {
      sub: 'mock-admin-sub',
      email: 'admin@example.com',
      name: 'Facilitator Admin',
      status: 'approved',
      createdAt: '2026-01-01T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      requestedCollegeName: null,
    },
  ]
  store.colleges = [
    {
      id: 'demo-stanford-2026',
      name: 'Stanford University',
      enabled: true,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'demo-mit-2026',
      name: 'MIT',
      enabled: true,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'demo-unscheduled',
      name: 'Reed College',
      enabled: true,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'demo-disabled',
      name: 'Disabled College Demo',
      enabled: false,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
  ]
  store.requests = []
  store.votes = {}
  store.aggregates = {}
  store.progress = {}
}

export function __getStore() {
  return store
}
